/**
 * Resource Cache Utilities
 * Provides centralized access to cached resources in localStorage
 * Resources are populated by ProviderLoadResourcesAll() during login
 */
import { readResourceCache } from './resourceCacheStore'

/**
 * Get a resource by type and ID from localStorage cache
 * @param {string} type - Resource type (agent, conference, extension, number)
 * @param {string} id - Resource ID to look up
 * @returns {Object|null} - Resource object or null if not found
 */
export const getResourceById = (type, id) => {
  if (!type || !id) return null

  // Map resource types to localStorage keys
  const cacheKeyMap = {
    agent: 'agents',
    conference: 'conferences',
    extension: 'extensions',
    number: 'numbers',
    // webchat: target is a Widget.ID; cache key populated by
    // LoadResourcesAll() in provider.js so AddressDisplay's link text can
    // show the widget's display name instead of a bare UUID.
    webchat: 'webchat_widgets',
  }

  const cacheKey = cacheKeyMap[type]
  if (!cacheKey) return null

  try {
    const cached = readResourceCache(cacheKey)
    if (!cached) return null

    const resources = JSON.parse(cached)

    // Handle both object (ParseData format) and array formats
    if (Array.isArray(resources)) {
      // Array format: search through items
      return resources.find(resource => resource.id === id) || null
    } else if (typeof resources === 'object' && resources !== null) {
      // Object format (ParseData): direct lookup by ID
      return resources[id] || null
    }

    return null
  } catch (error) {
    console.error(`Error reading ${cacheKey} from cache:`, error)
    return null
  }
}

/**
 * Get a resource name by type and ID
 * @param {string} type - Resource type (agent, conference, extension, number)
 * @param {string} id - Resource ID to look up
 * @returns {string|null} - Resource name or null if not found
 */
export const getResourceName = (type, id) => {
  const resource = getResourceById(type, id)
  return resource?.name || null
}

/**
 * Find a number resource by phone number target
 * @param {string} phoneNumber - Phone number to search for (e.g., "+821021656521")
 * @returns {Object|null} - Number resource object or null if not found
 */
export const findNumberByTarget = (phoneNumber) => {
  if (!phoneNumber) return null

  try {
    const cached = readResourceCache('numbers')
    if (!cached) return null

    const numbers = JSON.parse(cached)

    // Handle both object (ParseData format) and array formats
    if (Array.isArray(numbers)) {
      // Array format: search through items
      return numbers.find(num => num.number === phoneNumber) || null
    } else if (typeof numbers === 'object' && numbers !== null) {
      // Object format (ParseData): search through values
      return Object.values(numbers).find(num => num.number === phoneNumber) || null
    }

    return null
  } catch (error) {
    console.error('Error searching numbers cache:', error)
    return null
  }
}

/**
 * Check if a resource type is linkable
 * @param {string} type - Address type
 * @returns {boolean} - True if the type can be linked to a detail page
 */
export const isLinkableResourceType = (type) => {
  const linkableTypes = ['agent', 'conference', 'extension', 'tel', 'webchat', 'web_session']
  return linkableTypes.includes(type)
}

/**
 * Get resource ID for linking
 * For most types, returns the target as-is
 * For tel type, looks up the number in cache to get its ID
 * @param {string} type - Address type
 * @param {string} target - Address target value
 * @returns {string|null} - Resource ID for linking, or null if not linkable
 */
export const getResourceIdForLink = (type, target) => {
  if (!type || !target) return null

  // For tel type, need to find the number resource
  if (type === 'tel') {
    const numberResource = findNumberByTarget(target)
    return numberResource?.id || null
  }

  // For other types, target IS the resource ID
  return target
}

/**
 * Get all cached resources of a given type as an array.
 * Handles both the object (ParseData, {id: item}) and array cache formats
 * used by LoadResource()/LoadResourcesAll() in src/provider.js.
 * @param {string} cacheKey - localStorage key (e.g. 'calls', 'messages', 'numbers')
 * @returns {Array} - Array of resource objects, empty array if not cached or invalid
 */
export const getCachedResourceList = (cacheKey) => {
  if (!cacheKey) return []

  try {
    const cached = readResourceCache(cacheKey)
    if (!cached) return []

    const resources = JSON.parse(cached)

    if (Array.isArray(resources)) {
      return resources
    } else if (typeof resources === 'object' && resources !== null) {
      return Object.values(resources)
    }

    return []
  } catch (error) {
    console.error(`getCachedResourceList: Error reading '${cacheKey}' from cache:`, error)
    return []
  }
}
